import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Car, 
  Gauge, 
  Clock, 
  Activity,
  AlertCircle,
  Download,
  Trash2,
  Settings2,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { apiService } from '../services/api';
import { HistoryDetail as HistoryDetailType } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export function HistoryDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<HistoryDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!jobId) return;

    const loadDetail = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiService.getHistoryDetail(jobId);
        if (response.success && response.data) {
          setDetail(response.data);
        } else {
          setError(response.error || 'Error al cargar los detalles');
        }
      } catch (err) {
        setError('Error de conexión con el servidor');
        console.error('Error loading detail:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDetail();
  }, [jobId]);

  const handleDelete = async () => {
    if (!jobId) return;
    if (!confirm('¿Estás seguro de que deseas eliminar este vídeo del historial?')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await apiService.deleteFromHistory(jobId);
      if (response.success) {
        navigate('/history');
      } else {
        alert('Error al eliminar: ' + response.error);
      }
    } catch (err) {
      alert('Error al eliminar el vídeo');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeedColor = (speed: number) => {
    if (speed < 30) return 'text-green-400';
    if (speed < 60) return 'text-amber-400';
    if (speed < 90) return 'text-orange-400';
    return 'text-red-400';
  };

  const getVehicleTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      car: 'Coche',
      truck: 'Camión',
      bus: 'Autobús',
      motorcycle: 'Moto',
      bicycle: 'Bicicleta',
      van: 'Furgoneta',
      pickup: 'Pickup',
      suv: 'SUV',
      trailer: 'Remolque',
      emergency: 'Emergencia'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          <p className="text-dark-400">Cargando detalles...</p>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <p className="text-red-400">{error || 'Vídeo no encontrado'}</p>
          <Link
            to="/history"
            className="px-4 py-2 bg-dark-800 hover:bg-dark-700 text-dark-200 rounded-lg transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al historial
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/history"
            className="p-2 bg-dark-800 hover:bg-dark-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-dark-300" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-dark-100">
              {detail.filename || `Video ${detail.job_id}`}
            </h1>
            <p className="text-sm text-dark-500">{formatDate(detail.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {detail.processed_video_url && (
            <a
              href={apiService.getDownloadUrl(detail.job_id)}
              download={detail.filename ? `${detail.filename.replace(/\.[^/.]+$/, '')}_analyzed.mp4` : `video_${detail.job_id}_analyzed.mp4`}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Descargar Vídeo Analizado
            </a>
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {deleting ? (
              <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            Eliminar
          </button>
        </div>
      </div>

      {/* Video Player */}
      {detail.processed_video_url && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl overflow-hidden"
        >
          <video
            src={`${API_BASE_URL}${detail.processed_video_url}`}
            controls
            className="w-full aspect-video bg-black"
          />
        </motion.div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{detail.stats.total_vehicles}</p>
              <p className="text-xs text-dark-500">Vehículos</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Gauge className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{detail.stats.avg_speed.toFixed(1)}</p>
              <p className="text-xs text-dark-500">Vel. media (km/h)</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{detail.stats.max_speed.toFixed(1)}</p>
              <p className="text-xs text-dark-500">Vel. máxima (km/h)</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-dark-100">{formatDuration(detail.stats.video_duration)}</p>
              <p className="text-xs text-dark-500">Duración</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Processing Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-5 h-5 text-primary-400" />
            <h3 className="font-semibold text-dark-100">Información de Procesamiento</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-dark-400">Tiempo de procesamiento</span>
              <span className="text-dark-200 font-mono">
                {detail.stats.processing_time?.toFixed(1) || '-'} seg
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-400">FPS del vídeo</span>
              <span className="text-dark-200 font-mono">{detail.stats.fps || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-400">Total de frames</span>
              <span className="text-dark-200 font-mono">{detail.stats.frame_count || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-dark-400">Total de detecciones</span>
              <span className="text-dark-200 font-mono">{detail.stats.total_detections}</span>
            </div>
          </div>
        </motion.div>

        {/* Config Info */}
        {detail.config && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-4">
              <Settings2 className="w-5 h-5 text-primary-400" />
              <h3 className="font-semibold text-dark-100">Configuración Utilizada</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-dark-400">Confianza mínima</span>
                <span className="text-dark-200 font-mono">
                  {((detail.config.confidence_threshold as number) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Píxeles/metro</span>
                <span className="text-dark-200 font-mono">
                  {detail.config.pixels_per_meter as number}
                </span>
              </div>
              <div>
                <span className="text-dark-400 text-sm">Tipos de vehículo:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(detail.config.vehicle_classes as string[] || []).map((vc) => (
                    <span
                      key={vc}
                      className="px-2 py-0.5 bg-dark-800 text-dark-300 text-xs rounded"
                    >
                      {getVehicleTypeLabel(vc)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Vehicles List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="glass rounded-xl overflow-hidden"
      >
        <div className="px-4 py-3 border-b border-dark-800/50 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-400" />
          <h3 className="font-semibold text-dark-100">Vehículos Detectados</h3>
          <span className="text-sm text-dark-500">({detail.vehicles.length})</span>
        </div>

        {detail.vehicles.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-dark-400">No se detectaron vehículos en este vídeo</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-dark-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-dark-400 uppercase">Tipo</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-dark-400 uppercase">Vel. Media</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-dark-400 uppercase">Vel. Máx</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-dark-400 uppercase">Vel. Mín</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-dark-400 uppercase">Confianza</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-dark-400 uppercase">Detecciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-800/50">
                {detail.vehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-dark-800/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: vehicle.color
                              ? `rgb(${vehicle.color[0]}, ${vehicle.color[1]}, ${vehicle.color[2]})`
                              : '#6366f1'
                          }}
                        />
                        <span className="font-mono text-dark-200">{vehicle.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-dark-800 text-dark-300 text-xs rounded">
                        {getVehicleTypeLabel(vehicle.vehicle_type)}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${getSpeedColor(vehicle.avg_speed)}`}>
                      {vehicle.avg_speed.toFixed(1)} km/h
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${getSpeedColor(vehicle.max_speed)}`}>
                      {vehicle.max_speed.toFixed(1)} km/h
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-dark-300">
                      {vehicle.min_speed.toFixed(1)} km/h
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-dark-300">
                      {(vehicle.avg_confidence * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-dark-300">
                      {vehicle.detections_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default HistoryDetail;
